import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Loader2, Search, X } from 'lucide-react'

export interface SearchableOption {
  id: string
  label: string
  sublabel?: string
}

interface SearchableSelectProps {
  value: string
  onChange: (id: string) => void
  options: SearchableOption[]
  placeholder?: string
  loading?: boolean
  emptyText?: string
}

export default function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Search...',
  loading = false,
  emptyText = 'No results found',
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  const selected = options.find((o) => o.id === value)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filtered = query.trim()
    ? options.filter((o) => `${o.label} ${o.sublabel || ''}`.toLowerCase().includes(query.trim().toLowerCase()))
    : options

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 border border-[#e5e7eb] rounded-lg text-sm bg-white hover:border-[#d1d5db] transition"
      >
        <Search className="w-3.5 h-3.5 text-[#9ca3af] flex-shrink-0" />
        <span className={`flex-1 text-left truncate ${selected ? 'text-[#1f2937]' : 'text-[#9ca3af]'}`}>
          {selected ? selected.label : placeholder}
        </span>
        {selected && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              onChange('')
              setQuery('')
            }}
            className="p-0.5 hover:bg-[#f3f4f6] rounded"
          >
            <X className="w-3.5 h-3.5 text-[#9ca3af]" />
          </span>
        )}
        <ChevronDown className="w-3.5 h-3.5 text-[#9ca3af] flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-[#e5e7eb] rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-[#f3f4f6]">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter..."
              className="w-full px-2.5 py-1.5 border border-[#e5e7eb] rounded-md text-xs focus:outline-none focus:border-[#3b82f6]"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-3 text-xs text-[#6b7280]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-3 text-xs text-[#9ca3af]">{emptyText}</p>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    onChange(opt.id)
                    setOpen(false)
                    setQuery('')
                  }}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-[#eff6ff] transition ${
                    opt.id === value ? 'bg-[#eff6ff] text-[#1d4ed8]' : 'text-[#374151]'
                  }`}
                >
                  <p className="font-medium truncate">{opt.label}</p>
                  {opt.sublabel && <p className="text-[11px] text-[#6b7280] truncate">{opt.sublabel}</p>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
